// Rigid-train cascade.
//
// What "cascade" means here, decided with the planners:
//
//   When an activity's end moves LATER by delta, every activity after it in
//   the same area moves later by exactly the same delta. All of them, however
//   far into the future the plan runs. The spacing between activities is
//   preserved -- the whole downstream chain slides as one block.
//
// Two things follow from that, and both are deliberate:
//
//   * Gaps do NOT absorb the shift. The older behaviour only pushed the rows
//     it physically ran into, so a four-hour extension left a batch three
//     weeks out exactly where it was. That is not what a planner means by
//     "push the plan forward".
//
//   * There is no day limit. The previous implementation searched 90 days and
//     gave up after 14 empty ones, because scanning cost one HTTP request per
//     day. It now costs one request for the whole horizon, so the chain is
//     followed as far as the plan actually goes. That limit is what allowed
//     overlaps to appear a year out: rows past the window were never seen,
//     so the cascade wrote straight over them.
//
// Backwards movement is NOT a cascade. Shortening an activity or moving it
// earlier leaves a gap and nothing downstream changes. Pulling a plan earlier
// happens only through the explicit "Delete & Shift Back" button, which is a
// separate feature and untouched by this file.
import dayjs from 'dayjs';
import { toPlanActivity } from './cascadeFetch';

// The plan currently runs about a year out. Five years is "the whole plan"
// with room to spare, and costs the same single request either way.
export const HORIZON_YEARS = 5;

const startOf = (row) => dayjs(row.start_time || row.start_date);
const endOf = (row) => {
  const raw = row.end_time || row.end_date;
  return raw ? dayjs(raw) : startOf(row);
};

/**
 * Where a row sits relative to now.
 *
 *   finished - already ended. Never moved: you cannot reschedule work that is
 *              done. If a shift would land on one, the cascade refuses.
 *   running  - started but not finished. IS moved, by request: a planner
 *              pushing the day forward means the running batch too. This edits
 *              the plan, not the record of what happened -- actuals are a
 *              separate lane -- so nothing historical is rewritten.
 *   future   - has not started. Moved normally.
 */
export const classifyRow = (row, now = dayjs()) => {
  const nowMs = dayjs(now).valueOf();
  if (endOf(row).valueOf() <= nowMs) return 'finished';
  if (startOf(row).valueOf() <= nowMs) return 'running';
  return 'future';
};

/** How far the chain moves: always measured on the END time. */
export const deltaMinutes = (oldEnd, newEnd) => dayjs(newEnd).diff(dayjs(oldEnd), 'minute');

/**
 * Flatten an /activities-by-area response into plain plan rows.
 *
 * `unplanned` entries are log-book actuals with no plan row behind them, so
 * they are dropped -- `plan_id` is what tells a real plan row from one of
 * those. Rows are de-duplicated by id because the endpoint groups by area and
 * a row can appear under more than one bucket.
 */
export const parseAreaRows = (res) => {
  const seen = new Map();
  Object.values(res?.data || {})
    .flatMap((area) => [...(area?.matched || []), ...(area?.not_started || [])])
    .filter((row) => row && row.plan_id != null && row.plan_start_date)
    .forEach((row) => {
      if (!seen.has(row.plan_id)) seen.set(row.plan_id, toPlanActivity(row));
    });
  return [...seen.values()];
};

/**
 * The rows the cascade will move: everything in the area starting at or after
 * `fromTime`, minus the activity being edited.
 *
 * `fromTime` is the edited activity's ORIGINAL end. A row that starts before
 * that is either earlier in the plan or already overlapping the edited row,
 * and in both cases pushing it would be wrong -- it is not downstream.
 */
export const selectDownstream = ({ rows, fromTime, excludeIds = [] }) => {
  const excluded = new Set(
    (Array.isArray(excludeIds) ? excludeIds : [excludeIds])
      .filter((id) => id !== null && id !== undefined)
      .map(String)
  );
  const from = dayjs(fromTime).valueOf();
  return (rows || [])
    .filter((row) => row && !excluded.has(String(row.id)))
    .filter((row) => startOf(row).valueOf() >= from)
    .sort((a, b) => startOf(a).valueOf() - startOf(b).valueOf());
};

// Two ranges overlap when each starts before the other ends. Touching end-to-
// start is NOT an overlap -- back-to-back activities are the normal case. Same
// strict comparison the screen's own checkForOverlaps uses.
const overlaps = (aStart, aEnd, bStart, bEnd) =>
  aStart.valueOf() < bEnd.valueOf() && aEnd.valueOf() > bStart.valueOf();

/**
 * Work out the whole shift.
 *
 * @returns {{
 *   noop: boolean,          // delta <= 0: backwards is not a cascade
 *   shifts: Array,          // {activity, oldStart, oldEnd, newStart, newEnd, state}
 *   running: Array,         // the subset already in progress, listed separately
 *                           // so the confirm dialog can call them out
 *   skippedFinished: Array, // done, left where they are
 *   blockers: Array,        // finished rows the shift would land on -> refuse
 * }}
 *
 * Note there is no "movers collide with each other" case to handle: every
 * moving row moves by the same delta, so their spacing is unchanged and the
 * block cannot fold in on itself. The only thing that can be hit is something
 * that does not move.
 */
export const buildRigidShifts = ({
  rows,
  delta,
  now = dayjs(),
  editedOldStart = null,
  editedOldEnd = null,
  editedNewStart = null,
  editedNewEnd = null,
}) => {
  const empty = { noop: true, shifts: [], running: [], skippedFinished: [], blockers: [] };
  if (!Number.isFinite(delta) || delta <= 0) return empty;

  const shifts = [];
  const skippedFinished = [];

  (rows || []).forEach((row) => {
    const state = classifyRow(row, now);
    if (state === 'finished') {
      skippedFinished.push(row);
      return;
    }
    const oldStart = startOf(row);
    const oldEnd = endOf(row);
    shifts.push({
      activity: row,
      oldStart,
      oldEnd,
      newStart: oldStart.add(delta, 'minute'),
      newEnd: oldEnd.add(delta, 'minute'),
      state,
    });
  });

  // Anything finished that the shift would NEWLY sit on top of.
  //
  // "Newly" matters. Everything moves forward, away from the past, so a mover
  // can only reach a finished row if it was already overlapping it. There are
  // real, historical overlaps in the data, and refusing every edit because of
  // one that predates the change would block work nobody can fix. So a
  // finished row counts as a blocker only when the overlap did not exist
  // before -- this edit is what created it.
  const blockers = skippedFinished.filter((done) => {
    const dStart = startOf(done);
    const dEnd = endOf(done);

    const editedHitsNow = editedNewStart && editedNewEnd
      && overlaps(dayjs(editedNewStart), dayjs(editedNewEnd), dStart, dEnd);
    const editedHitBefore = editedOldStart && editedOldEnd
      && overlaps(dayjs(editedOldStart), dayjs(editedOldEnd), dStart, dEnd);
    if (editedHitsNow && !editedHitBefore) return true;

    return shifts.some((s) => overlaps(s.newStart, s.newEnd, dStart, dEnd)
      && !overlaps(s.oldStart, s.oldEnd, dStart, dEnd));
  });

  return {
    noop: false,
    shifts,
    running: shifts.filter((s) => s.state === 'running'),
    skippedFinished,
    blockers,
  };
};

export default { classifyRow, deltaMinutes, parseAreaRows, selectDownstream, buildRigidShifts, HORIZON_YEARS };
